import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, Layers, Lock, Sparkles } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData, createCourseSchema } from '../components/seo/StructuredData';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { courseService } from '../services/firebase/courses';
import { Course } from '../types/course';

const PRERENDER_READY_FLAG = '__PRERENDER_READY__';
const SITE_URL = 'https://trainedup.ai';

const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return 'Self-paced';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

interface CtaConfig {
  label: string;
  to: string;
  variant: 'primary' | 'outline';
  icon: React.ReactNode;
}

const buildCta = (
  course: Course,
  isAuthenticated: boolean,
  isFreePlan: boolean
): CtaConfig => {
  const courseUrl = `/courses/${course.id}`;
  const isPaidCourse = !course.isFree && course.requiredSubscriptionTier !== 'free';

  if (!isAuthenticated) {
    return {
      label: isPaidCourse ? 'Sign up to unlock' : 'Sign up to start',
      to: `/register?next=${encodeURIComponent(courseUrl)}`,
      variant: 'primary',
      icon: <Sparkles className="w-4 h-4" />,
    };
  }

  if (isPaidCourse && isFreePlan) {
    return {
      label: 'Upgrade to access',
      to: '/pricing',
      variant: 'outline',
      icon: <Lock className="w-4 h-4" />,
    };
  }

  return {
    label: 'Open course',
    to: courseUrl,
    variant: 'primary',
    icon: <BookOpen className="w-4 h-4" />,
  };
};

export const CourseCatalogPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { getCurrentTier } = useSubscription();
  const isFreePlan = getCurrentTier() === 'free';

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { courses: fetched } = await courseService.getCourses(
          { status: 'published' },
          { pageSize: 1000 }
        );
        if (cancelled) return;
        setCourses(fetched);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load course catalog:', err);
        setError('Unable to load the course catalog right now. Please try again shortly.');
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Signal readiness to the prerender script once the courses are in the DOM.
  useEffect(() => {
    if (loading) return;
    if (typeof window !== 'undefined') {
      (window as any)[PRERENDER_READY_FLAG] = true;
    }
  }, [loading]);

  const itemListSchema = useMemo(() => {
    if (courses.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: courses.map((course, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: createCourseSchema({
          name: course.title,
          description: course.shortDescription || course.description,
          provider: 'trAInedup.ai',
          url: `${SITE_URL}/courses/${course.id}`,
          image: course.thumbnail || course.coverImage,
          numberOfLessons: course.totalModules,
          timeRequired: course.estimatedDuration
            ? `PT${Math.max(1, Math.round(course.estimatedDuration / 60))}H`
            : undefined,
          educationalLevel: course.difficulty,
        }),
      })),
    };
  }, [courses]);

  return (
    <Layout>
      <SEOHead
        title="AI Course Catalog — Browse Every trAInedup.ai Course"
        description="Browse the complete trAInedup.ai course catalog. Practical AI courses for professionals, freelancers and small businesses — from prompt engineering to AI strategy and ethics."
        canonical={`${SITE_URL}/course-catalog`}
        ogType="website"
        keywords={[
          'AI courses',
          'AI training',
          'prompt engineering course',
          'AI for business',
          'learn AI online',
        ]}
      />
      {itemListSchema && <StructuredData data={itemListSchema} />}

      <section className="bg-gradient-to-b from-primary/5 to-white">
        <div className="container-custom py-14">
          <header className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">
              Full Catalog
            </p>
            <h1 className="mt-2 font-montserrat text-4xl md:text-5xl font-bold text-neutral-900">
              Every AI course we offer, in one place
            </h1>
            <p className="mt-4 text-lg text-neutral-600">
              Practical, hands-on AI training for busy professionals, freelancers, and small
              businesses. Browse every published course on trAInedup.ai — from foundations
              and ethics to prompt engineering, AI strategy, and applied workflows.
            </p>
          </header>
        </div>
      </section>

      <section className="container-custom py-12">
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Card key={idx} className="animate-pulse h-72">
                <span className="sr-only">Loading course</span>
              </Card>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && courses.length === 0 && (
          <div className="rounded-lg border border-neutral-200 bg-white px-6 py-10 text-center">
            <h2 className="font-montserrat text-xl font-semibold text-neutral-900">
              New courses coming soon
            </h2>
            <p className="mt-2 text-neutral-600">
              We're building out the catalog. Check back shortly.
            </p>
          </div>
        )}

        {!loading && !error && courses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => {
              const cta = buildCta(course, isAuthenticated, isFreePlan);
              const isPaidCourse =
                !course.isFree && course.requiredSubscriptionTier !== 'free';

              return (
                <Card
                  key={course.id}
                  className="flex flex-col overflow-hidden hover:shadow-lg transition-shadow duration-200"
                >
                  {course.thumbnail && (
                    <div className="aspect-video w-full overflow-hidden bg-neutral-100">
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1 flex flex-col p-5">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isPaidCourse
                            ? 'bg-primary/10 text-primary'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {isPaidCourse ? 'Premium' : 'Free'}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                        <Layers className="w-3.5 h-3.5" />
                        {course.difficulty || 'All levels'}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(course.estimatedDuration)}
                      </span>
                    </div>

                    <h2 className="font-montserrat text-lg font-semibold text-neutral-900">
                      {course.title}
                    </h2>
                    <p className="mt-2 text-sm text-neutral-600 line-clamp-4">
                      {course.description || course.shortDescription}
                    </p>

                    <div className="mt-auto pt-5">
                      <Link to={cta.to} className="block">
                        <Button
                          variant={cta.variant}
                          className="w-full inline-flex items-center justify-center gap-2"
                        >
                          {cta.icon}
                          {cta.label}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default CourseCatalogPage;
